import { criterion, fromCriteria } from "@/core/check";
import type { Lesson } from "@/core/types";
import { pruefeMitMakro } from "@/office/makro/pruefung";
import { readDocument } from "@/office/word/document";
import { MAKRO_AUTOKORREKTUR } from "../makros";

export const lektionAutokorrektur: Lesson = {
  id: "autokorrektur",
  title: "AutoKorrektur und AutoFormat",
  summary: "Was Word beim Tippen ungefragt ändert – und wie man das für sich nutzt.",
  durationMinutes: 8,
  learningGoals: [
    "Einen eigenen AutoKorrektur-Eintrag anlegen",
    "AutoFormat-Änderungen erkennen und mit Strg + Z rückgängig machen",
  ],
  steps: [
    {
      id: "eintrag-anlegen",
      title: "Ein eigener AutoKorrektur-Eintrag",
      explanation: [
        "Die AutoKorrektur ersetzt Tippfehler und Kürzel, sobald ein Wort abgeschlossen " +
          "ist: „dei“ wird zu „die“, (c) zu ©. Die Liste steht unter „Datei“ → " +
          "„Optionen“ → „Dokumentprüfung“ → „AutoKorrektur-Optionen“.",
        "Dort lassen sich eigene Einträge anlegen. Ein Kürzel sollte etwas sein, das man nie " +
          "als normales Wort tippt – ein vorangestelltes Zeichen wie # ist ein bewährter " +
          "Trick. Aus „#gruss“ wird dann „Mit freundlichen Grüßen“.",
        "Die Liste gilt für alle Office-Programme auf dem Rechner, nicht nur für dieses " +
          "Dokument.",
      ],
      keyPoints: [
        "Datei → Optionen → Dokumentprüfung → AutoKorrektur-Optionen.",
        "Kürzel mit Sonderzeichen, damit sie nie versehentlich auslösen.",
        "Die Liste gilt für alle Office-Programme.",
      ],
      task: {
        instruction:
          "Lege den AutoKorrektur-Eintrag „#gruss“ an, der durch deine Grußformel ersetzt " +
          "wird, und tippe „#gruss“ danach einmal in dein Dokument.",
        hint: "AutoKorrektur-Optionen → „Ersetzen“: #gruss, „Durch“: Mit freundlichen Grüßen → Hinzufügen.",
        macro: MAKRO_AUTOKORREKTUR,
        check: async () => {
          const text = (await readDocument()).text;
          return pruefeMitMakro(MAKRO_AUTOKORREKTUR, (bericht) => {
            const ersetzung = (bericht.text("ersetzung") ?? "").trim();
            return fromCriteria(
              [
                criterion("Der Eintrag „#gruss“ ist angelegt", bericht.janein("gefunden") === true),
                criterion(
                  "Die Ersetzung steht im Dokument",
                  ersetzung.length > 0 && text.includes(ersetzung.split(/\r|\n/)[0]),
                ),
                criterion("Im Dokument steht kein unersetztes „#gruss“", !text.toLowerCase().includes("#gruss")),
              ],
              {
                success: "Vier Tasten statt eines ganzen Satzes – in jedem Office-Programm.",
                failure: "Noch nicht ganz.",
                hint:
                  "Die AutoKorrektur greift erst nach einem Leerzeichen oder Satzzeichen hinter " +
                  "dem Kürzel. Ist „Während der Eingabe ersetzen“ ausgeschaltet, passiert nichts.",
              },
            );
          });
        },
      },
    },
    {
      id: "autoformat",
      title: "AutoFormat während der Eingabe",
      explanation: [
        "Neben der AutoKorrektur formatiert Word beim Tippen: Ein Absatz, der mit „1.“ " +
          "oder „-“ beginnt, wird zur Liste. Drei Bindestriche und Enter werden zu einer " +
          "Rahmenlinie. Gerade Anführungszeichen werden zu typografischen. Diese Regeln " +
          "stehen auf der Registerkarte „AutoFormat während der Eingabe“.",
        "Die meisten davon helfen – eine automatische Liste ist eine echte Liste, eine " +
          "Rahmenlinie eine echte Rahmenlinie. Wenn eine Änderung unerwünscht ist, macht " +
          "Strg + Z direkt danach nur die automatische Änderung rückgängig, nicht das " +
          "Getippte.",
      ],
      keyPoints: [
        "Strg + Z direkt nach der Änderung nimmt nur die Automatik zurück.",
        "Einstellungen: AutoKorrektur-Optionen → AutoFormat während der Eingabe.",
      ],
    },
  ],
};
